import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/semanas";

let seq = 0;
const newRel = (pct) => ({ key: ++seq, estado: "", muni: "", pct, pctDefault: !pct });
const r2 = (v) => Math.round(v * 100) / 100;
const fromRow = (row) => (row.relaciones || []).map((r) => ({ key: ++seq, estado: r.estado, muni: `${r.region}|${r.municipio}`, pct: r.pct }));
const splitMuni = (m) => [Number(m.split("|")[0]), m.slice(m.indexOf("|") + 1)];
// Precio ponderado exacto en enteros y redondeo a centavos (mitad hacia arriba), igual que el servidor.
const weighted = (prices, rels, f) => Math.round(prices.reduce((s, p, i) => s + Math.round(p[f] * 100) * Math.round(rels[i].pct * 100), 0) / 10000) / 100;

function RelRow({ rel, idx, estados, cat, used, onChange, onRemove }) {
  const munis = cat.filter((c) => c.estado === rel.estado);
  const ref = rel.muni ? cat.find((c) => `${c.region}|${c.municipio}` === rel.muni) : null;
  const setPct = (v) => onChange({ ...rel, pctDefault: false, pct: Number.isFinite(v) ? Math.min(100, Math.max(0, v)) : 0 });
  const [vacio, setVacio] = React.useState(false);
  return (
    <div className="rounded-xl border border-slate-200 p-3 space-y-3" data-testid={`rel-row-${idx}`}>
      <div className="grid grid-cols-[1fr_1.3fr_auto] gap-2">
        <Select value={rel.estado} onValueChange={(v) => onChange({ ...rel, estado: v, muni: "" })}>
          <SelectTrigger data-testid={`rel-estado-${idx}`}><SelectValue placeholder="Entidad federativa" /></SelectTrigger>
          <SelectContent className="max-h-72">{estados.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={rel.muni} onValueChange={(v) => onChange({ ...rel, muni: v })} disabled={!rel.estado}>
          <SelectTrigger data-testid={`rel-municipio-${idx}`}><SelectValue placeholder="Municipio" /></SelectTrigger>
          <SelectContent className="max-h-72">
            {munis.map((m) => {
              const k = `${m.region}|${m.municipio}`;
              const dup = used.has(`${m.estado}|${m.municipio}`) && k !== rel.muni;
              return <SelectItem key={k} value={k} disabled={dup}>{m.municipio} · Región {m.region}{dup ? " (ya asignado)" : ""}</SelectItem>;
            })}
          </SelectContent>
        </Select>
        <button onClick={onRemove} data-testid={`rel-remove-${idx}`} title="Eliminar relación" className="h-10 w-10 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
      </div>
      {rel.muni && !ref && (
        <p className="text-xs text-amber-700 flex items-center gap-1" data-testid={`rel-missing-${idx}`}><AlertTriangle className="w-3.5 h-3.5" />{splitMuni(rel.muni)[1]}, {rel.estado} (región {splitMuni(rel.muni)[0]}) no tiene precio en la publicación seleccionada.</p>
      )}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-slate-500">
        <span>Región CNE: <b className="text-slate-800" data-testid={`rel-region-${idx}`}>{ref?.region ?? "—"}</b></span>
        <span>Oficial/kg: <b className="text-slate-800" data-testid={`rel-kg-${idx}`}>{ref ? money(ref.precio_kg) : "—"}</b></span>
        <span>Oficial/litro: <b className="text-slate-800" data-testid={`rel-lt-${idx}`}>{ref ? money(ref.precio_lt) : "—"}</b></span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs font-semibold text-slate-500 w-28">Ponderación (%)</span>
        <Slider value={[rel.pct]} min={0} max={100} step={0.01} onValueChange={([v]) => setPct(r2(v))} className="flex-1" data-testid={`rel-slider-${idx}`} />
        <input type="number" min={0} max={100} step={0.01} value={vacio ? "" : rel.pct} onFocus={() => { if (rel.pctDefault && !rel.pct) setVacio(true); }} onBlur={() => setVacio(false)}
          onChange={(e) => { setVacio(false); setPct(e.target.value === "" ? 0 : Number(e.target.value)); }}
          data-testid={`rel-pct-${idx}`} className="h-9 w-24 rounded-lg border border-slate-200 bg-slate-50 px-2 text-sm text-right text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-red-200" />
      </div>
    </div>
  );
}

function Summary({ rels, cat }) {
  const total = rels.reduce((s, r) => s + Math.round(r.pct * 1e6), 0) / 1e6;
  const diff = r2(100 - total);
  const prices = rels.map((r) => cat.find((c) => `${c.region}|${c.municipio}` === r.muni));
  const complete = rels.length > 0 && prices.every(Boolean);
  const kg = complete ? weighted(prices, rels, "precio_kg") : null;
  const lt = complete ? weighted(prices, rels, "precio_lt") : null;
  const ok = Math.abs(diff) < 1e-9;
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm space-y-1" data-testid="rel-summary">
      <div className="flex flex-wrap justify-between gap-2">
        <span>Relaciones: <b data-testid="rel-count">{rels.length}</b></span>
        <span data-testid="rel-total" className={ok ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"}>
          Ponderación total: {r2(total)}% / 100% — {ok ? "Completa" : diff > 0 ? `Faltante: ${diff}%` : `Excedente: ${-diff}%`}
        </span>
      </div>
      <div className="flex flex-wrap gap-x-6">
        <span>Ponderado/kg: <b data-testid="rel-pond-kg">{kg == null ? "—" : money(kg)}</b></span>
        <span>Ponderado/litro: <b data-testid="rel-pond-lt">{lt == null ? "—" : money(lt)}</b></span>
      </div>
      {!ok && rels.length > 0 && <p className="text-xs text-amber-700 flex items-center gap-1" data-testid="rel-error"><AlertTriangle className="w-3.5 h-3.5" />La suma de ponderaciones debe ser exactamente 100% para poder guardar.</p>}
      {ok && <p className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Ponderación válida.</p>}
    </div>
  );
}

export function RelacionesDialog({ row, pubId, structure, onClose }) {
  const qc = useQueryClient();
  const [rels, setRels] = useState(() => fromRow(row));
  const [saving, setSaving] = useState(false);
  const { data: cat = [], isLoading } = useQuery({
    queryKey: ["cne", "catalogo", pubId],
    queryFn: async () => (await api.get("/control-semanal/cne/catalogo", { params: { pub_id: pubId } })).data.items,
  });
  const estados = useMemo(() => [...new Set(cat.map((c) => c.estado))].sort(), [cat]);
  const used = new Set(rels.filter((r) => r.muni).map((r) => `${r.estado}|${splitMuni(r.muni)[1]}`));
  const total = rels.reduce((s, r) => s + Math.round(r.pct * 1e6), 0) / 1e6;
  const valid = rels.every((r) => r.muni) && (rels.length === 0 || Math.abs(total - 100) < 1e-9);
  const add = () => setRels([...rels, newRel(rels.length ? Math.max(0, r2(100 - total)) : 100)]);
  const save = async () => {
    setSaving(true);
    try {
      const relaciones = rels.map((r) => { const [region, municipio] = splitMuni(r.muni); return { estado: r.estado, municipio, region, pct: r.pct }; });
      await api.put("/control-semanal/cne/plantas", { planta: row.planta, structure, relaciones });
      toast.success(relaciones.length ? "Relaciones territoriales guardadas." : "Asignación eliminada.");
      qc.invalidateQueries({ queryKey: ["cne", "plantas"] });
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "No se pudo guardar.");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-white text-slate-900 max-h-[90vh] overflow-y-auto" data-testid="cne-asignar-dialog">
        <DialogHeader><DialogTitle>Relaciones territoriales · {row.planta}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500 -mt-2">Asigna uno o varios municipios oficiales y su ponderación. La región y los precios se toman de la publicación oficial seleccionada.</p>
        {isLoading ? <Loader2 className="w-5 h-5 text-red-600 animate-spin" /> : (
          <div className="space-y-3">
            {rels.map((r, i) => (
              <RelRow key={r.key} rel={r} idx={i} estados={estados} cat={cat} used={used}
                onChange={(nr) => setRels(rels.map((x) => (x.key === r.key ? nr : x)))}
                onRemove={() => setRels(rels.filter((x) => x.key !== r.key))} />
            ))}
            {!rels.length && <p className="text-sm text-slate-400 italic" data-testid="rel-empty">Sin relaciones: al guardar, la planta quedará «Sin asignar».</p>}
            <button onClick={add} data-testid="rel-add-btn" className="h-9 px-3 rounded-lg border border-dashed border-slate-300 text-sm font-semibold text-slate-600 hover:bg-slate-50 flex items-center gap-1.5"><Plus className="w-4 h-4" />Agregar relación</button>
            <Summary rels={rels} cat={cat} />
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} data-testid="rel-cancel" className="h-9 px-3 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">Cancelar</button>
          <button onClick={save} disabled={saving || !valid} data-testid="cne-asignar-save" className="h-9 px-4 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">{saving && <Loader2 className="w-4 h-4 animate-spin" />}Guardar</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
